import React, { useState, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  SlInput,
  SlButton,
  SlDrawer,
  SlTooltip,
  SlIcon
} from "@shoelace-style/shoelace/dist/react";
import api from "../../services/api";
import { Toast } from "../../shared/Toast";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import { DatetoTimestamp, formatDateWithTimeYear } from "../../utils/dateFormat";
import Paginator from "../../shared/Paginator";
import config from "../../config";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { roles } from "../../utils/constants";

function CashfreePaymentLinkCreation() {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [paymentArray, setPaymentArray] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [initialRender, setInitialRender] = useState(true);
  const [searchString, setSearchString] = useState("");
  const [addDirectLink, setAddDirectLink] = useState("");
  const [downloadData, setDownloadData] = useState([])
  const [downloadLoader, setDownloadLoader] = useState(false)
  const csvLinkRef = useRef(); // Create a ref for CSVLink

  let csvHeaders = [
    { label: "Order Id", key: "order_id" },
    { label: "Link Id", key: "link_id" },
    { label: "Cashfree Link Id", key: "cf_link_id" },
    { label: "Status", key: "link_status" },
    { label: "Amount", key: "link_amount" },
    { label: "Purpose", key: "link_purpose" },
    { label: "Link URL", key: "link_url" },
    { label: "Created By", key: "created_by_name" },
    { label: "Customer Name", key: "customer_name" },
    { label: "Customer Email", key: "customer_email" },
    { label: "Customer Phone", key: "customer_phone" },
    { label: "Created At", key: "created_on" }
  ]

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };
  const addPaymentLink = async (newPaymentDetails) => {
    setLoading(true);
    try {
      let res = await api({
        url: "/order/add-payment-link",
        method: "POST",
        responseType: "json",
        data: { ...newPaymentDetails },
      });
      if (res.code === 200) {
        Toast("success", "Payment link added successfully");
        getPaymentLink();
      } else {
        Toast(
          "error",
          res.data?.message || res.message || "Failed to add payment link"
        );
      }
    } catch (error) {
      console.error("Error @ addPaymentLinks", error);
      Toast("error", "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };
  const deleteOrderPaymentLink = async (opd_id) => {
    setFetchLoading(true);
    try {
      let res = await api({
        url: "/order/delete-payment-link",
        method: "POST",
        responseType: "json",
        data: { opd_id },
      });
      if (res.code === 200) {
        Toast("success", "Payment link deleted successfully");
        getPaymentLink();
      } else {
        Toast(
          "error",
          res.data?.message || res.message || "Failed to delete payment link"
        );
        setFetchLoading(false);
      }
    } catch (error) {
      console.error("Error @ deleteOrderPaymentLink", error);
      Toast("error", "Something went wrong!");
      setFetchLoading(false);
    }
  };
  // Validation schema
  const validationSchema = Yup.object({
    link_amount: Yup.string().required("Amount is required"),
    link_purpose: Yup.string().required("Link purpose is required"),
    customer_phone: Yup.string().required("Customer phone is required").min(10, "Customer phone must be 10 digits").max(10, "Customer phone must be 10 digits"),
    customer_name: Yup.string(),
    customer_email: Yup.string().email("Invalid email format"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      link_amount: "",
      link_purpose: "",
      customer_email: "",
      customer_name: "",
      customer_phone: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await addPaymentLink(values);
        resetForm();
        setShowForm(false);
      } catch (error) {
        console.error("Error in form submission:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // const editOrderPaymentLink = async(link_id, opd_id)=>{
  //   try {
  //     let res = await api({
  //       url: "/order/edit-payment-link",
  //       method: "POST",
  //       responseType: "json",
  //       data: { link_id, opd_id }
  //     })
  //     if(res.code === 200){
  //       Toast("success", "Payment link edited successfully")
  //       getPaymentLink(order_id)
  //       setEditPaymentOpdId(null)
  //       setEditPaymentLinkId(null)
  //     }else{
  //       Toast("error", res.message)
  //       setEditPaymentOpdId(null)
  //       setEditPaymentLinkId(null)
  //     }
  //   } catch (error) {
  //     console.error("Error @ editOrderPaymentLink", error)
  //     Toast("error", "Something went wrong!")
  //   }
  // }
  const getPaymentLink = async () => {
    setFetchLoading(true);
    try {
      let res = await api({
        url: "/order/get-payment-links",
        method: "POST",
        responseType: "json",
        data: { currentPage, limit: config.PER_PAGE, searchString },
      });
      if (res.code === 200) {
        setPaymentArray(res.data?.paymentLinks || []);
        setTotalPages(Math.ceil(res.data?.totalCount / config.PER_PAGE) || 0);
      } else {
        Toast("error", res.message || "Failed to fetch payment links");
      }
      setInitialRender(false);
    } catch (error) {
      console.error("Error @ getPaymentLink", error);
      Toast("error", "Something went wrong!");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleAddDirectLink = async () => {
    try {
      let res = await api({
        url: "/order/add-direct-link",
        method: "POST",
        responseType: "json",
        data: { link_id: addDirectLink },
      });
      if (res.code === 200) {
        Toast("success", "Direct link added successfully");
        getPaymentLink();
        setAddDirectLink("");
        setShowForm(false)
      } else {
        Toast("error", res.message || "Failed to add direct link");
      }
    } catch (error) {
      console.error("Error @ handleAddDirectLink", error);
    }
  }
  const download = async () => {
    try {
      setDownloadLoader(true)
      let res = await api({
        url: "/order/get-payment-links",
        method: "POST",
        responseType: "json",
        data: { searchString },
      });
      if (res.code === 200) {
        setDownloadData(res.data?.paymentLinks)
      } else {
        Toast("error", res.message || "Failed to fetch payment links");
      }
      setDownloadLoader(false)
    } catch (error) {
      console.error("Error @ download", error);
    }
  }



  useEffect(() => {
    if (downloadData.length) {
        csvLinkRef?.current?.link?.click();
        setDownloadData([]);
    }
}, [downloadData]);

  useEffect(() => {
    getPaymentLink();
  }, []);
  useEffect(() => {
    if (!initialRender) getPaymentLink();
  }, [currentPage, searchString]);
  return (
    <div
      className="d-flex gap-2.5 flex-wrap"
      style={{
        padding: "20px",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <CSVLink
        data={downloadData.map(
          (item) => {

            let obj = item;
            item.created_on = formatDateWithTimeYear(item.created_on);
            return obj;
          }
        )}
        headers={csvHeaders}
        filename={"Cashfree_links.csv"}
        ref={csvLinkRef} // Assign the ref to the CSVLink component
      ></CSVLink>
      <div className="d-flex justify-end mb-3 w-full gap-2 items-center" style={{ marginBottom: "20px" }}>
        <SlInput
          type="text"
          placeholder="Search by order id/link id/cashfree link id/customer name/customer email/customer phone"
          value={searchString}
          onSlInput={(e) => setSearchString(e.target.value)}
        />
        {localStorage.getItem("role") === roles.ops ? <SlButton variant="primary" onClick={() => setShowForm(true)}>
          + Add Payment Link
        </SlButton> : null}
        {downloadLoader ? (
          <TableLoader />
        ) : (
          <SlTooltip content="Download">
            <SlIcon
              library="lucide"
              name="download"
              className="w-6 h-6"
              onClick={() => {
                download()
              }}
            ></SlIcon>
          </SlTooltip>
        )}
      </div>
      <div
        className="w-full"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          padding: "24px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          overflowX: "auto",
        }}
      >
        {/* ==== ADD PAYMENT LINK BUTTON ==== */}

        {/* ==== TABLE LIST ==== */}
        <table
          className="table table-bordered"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #dee2e6",
              }}
            >
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                S.NO
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Order Id
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Link Id
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Cashfree Link Id
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Status
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Amount
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Purpose
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Link URL
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Created By
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Customer Name
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Customer Email
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Customer Phone
              </th>
              <th
                style={{
                  padding: "12px 16px",
                  textAlign: "left",
                  fontWeight: "600",
                  color: "#495057",
                  fontSize: "14px",
                }}
              >
                Created At
              </th>
              {/* {localStorage.getItem("role") === roles.ops && <th>Actions</th>} */}
            </tr>
          </thead>

          {fetchLoading ? (
            <TableLoader colspan={8} />
          ) : paymentArray?.length > 0 ? (
            <tbody>
              {paymentArray.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: "1px solid #dee2e6",
                    transition: "background-color 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#ffffff")
                  }
                >
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    {index + 1}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                      fontWeight: "500",
                    }}
                  >
                    {item.order_id || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                      fontWeight: "500",
                    }}
                  >
                    {item.link_id}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                      fontWeight: "500",
                    }}
                  >
                    {item.cf_link_id || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        backgroundColor:
                          item.link_status === "ACTIVE"
                            ? "#d4edda"
                            : item.link_status === "PAID"
                              ? "#d1ecf1"
                              : "#f8d7da",
                        color:
                          item.link_status === "ACTIVE"
                            ? "#155724"
                            : item.link_status === "PAID"
                              ? "#0c5460"
                              : "#721c24",
                      }}
                    >
                      {item.link_status || "-"}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                      fontWeight: "500",
                    }}
                  >
                    {item.link_amount || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    {item.link_purpose || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    <div className="d-flex">
                      <div>
                      {item.link_url || "-"}
                      </div>
                      {item.link_url && <button onClick={() => copyToClipboard(item.link_url)} className="ml-2 text-gray-400 hover:text-gray-600 transition" title="Copy Link Id">
                        📋
                      </button>}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    {item.created_by_name || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    {item.customer_name || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    {item.customer_email || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    {item.customer_phone || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 16px",
                      fontSize: "14px",
                      color: "#495057",
                    }}
                  >
                    {formatDateWithTimeYear(item?.created_on) || "-"}
                  </td>
                  {/* {localStorage.getItem("role") === roles.ops && (
                    <td>
                      <SlButton
                        variant="danger"
                        size="small"
                        className="cursor-pointer"
                        onClick={() =>
                          deleteOrderPaymentLink(item.opd_id)
                        }
                      >
                        Delete
                      </SlButton>
                    </td>
                  )} */}
                </tr>
              ))}
            </tbody>
          ) : (
            <NoDataFound colspan={8} />
          )}
        </table>
      </div>
      <div className="flex justify-end w-full">
        <Paginator
          pageCount={totalPages}
          handlePageClick={handlePageClick}
          forcePage={currentPage}
        />
      </div>
      {/* ==== DRAWER FOR ADD PAYMENT LINK FORM ==== */}
      <SlDrawer
        label="Add New Payment Link"
        open={showForm}
        onSlRequestClose={() => {
          setShowForm(false);
          formik.resetForm();
        }}
        style={{
          "--size": "50%",
          padding: "24px",
        }}
      >
        <form onSubmit={formik.handleSubmit} style={{ padding: "20px 0" }}>
          <div
            className="grid grid-cols-2 gap-3 mt-3"
            style={{ gap: "20px", marginTop: "20px" }}
          >
            {/* Amount */}
            <div style={{ marginBottom: "16px" }}>
              <SlInput
                label="Amount"
                type="number"
                placeholder="Enter amount"
                value={formik.values.link_amount}
                required
                className={
                  formik.errors.link_amount && formik.touched.link_amount
                    ? "error"
                    : ""
                }
                onSlInput={(e) => {
                  formik.setFieldValue("link_amount", e.target.value);
                }}
                onBlur={formik.handleBlur}
                name="link_amount"
                style={{
                  "--sl-input-border-radius": "6px",
                  "--sl-input-border-width": "1px",
                  "--sl-input-border-color":
                    formik.errors.link_amount && formik.touched.link_amount
                      ? "#dc3545"
                      : "#ced4da",
                }}
              />
              {formik.errors.link_amount && formik.touched.link_amount && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px",
                    fontWeight: "500",
                  }}
                >
                  {formik.errors.link_amount}
                </div>
              )}
            </div>

            {/* Customer Name */}
            <div style={{ marginBottom: "16px" }}>
              <SlInput
                label="Customer Name"
                placeholder="Enter customer's name"
                value={formik.values.customer_name}
                className={
                  formik.errors.customer_name && formik.touched.customer_name
                    ? "error"
                    : ""
                }
                onSlInput={(e) => {
                  formik.setFieldValue("customer_name", e.target.value);
                }}
                onBlur={formik.handleBlur}
                name="customer_name"
                style={{
                  "--sl-input-border-radius": "6px",
                  "--sl-input-border-width": "1px",
                  "--sl-input-border-color":
                    formik.errors.customer_name && formik.touched.customer_name
                      ? "#dc3545"
                      : "#ced4da",
                }}
              />
              {formik.errors.customer_name && formik.touched.customer_name && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px",
                    fontWeight: "500",
                  }}
                >
                  {formik.errors.customer_name}
                </div>
              )}
            </div>

            {/* Customer Email */}
            <div style={{ marginBottom: "16px" }}>
              <SlInput
                label="Customer Email"
                type="email"
                placeholder="Enter email"
                value={formik.values.customer_email}
                className={
                  formik.errors.customer_email && formik.touched.customer_email
                    ? "error"
                    : ""
                }
                onSlInput={(e) => {
                  formik.setFieldValue("customer_email", e.target.value);
                }}
                onBlur={formik.handleBlur}
                name="customer_email"
                style={{
                  "--sl-input-border-radius": "6px",
                  "--sl-input-border-width": "1px",
                  "--sl-input-border-color":
                    formik.errors.customer_email &&
                      formik.touched.customer_email
                      ? "#dc3545"
                      : "#ced4da",
                }}
              />
              {formik.errors.customer_email &&
                formik.touched.customer_email && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "4px",
                      fontWeight: "500",
                    }}
                  >
                    {formik.errors.customer_email}
                  </div>
                )}
            </div>

            {/* Customer Phone */}
            <div style={{ marginBottom: "16px" }}>
              <SlInput
                label="Customer Phone"
                type="tel"
                placeholder="Enter phone number"
                value={formik.values.customer_phone}
                required
                className={
                  formik.errors.customer_phone && formik.touched.customer_phone
                    ? "error"
                    : ""
                }
                onSlInput={(e) => {
                  formik.setFieldValue("customer_phone", e.target.value);
                }}
                onBlur={formik.handleBlur}
                name="customer_phone"
                style={{
                  "--sl-input-border-radius": "6px",
                  "--sl-input-border-width": "1px",
                  "--sl-input-border-color":
                    formik.errors.customer_phone &&
                      formik.touched.customer_phone
                      ? "#dc3545"
                      : "#ced4da",
                }}
              />
              {formik.errors.customer_phone &&
                formik.touched.customer_phone && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "4px",
                      fontWeight: "500",
                    }}
                  >
                    {formik.errors.customer_phone}
                  </div>
                )}
            </div>

            {/* Link Purpose */}
            <div style={{ marginBottom: "16px" }}>
              <SlInput
                label="Link Purpose"
                placeholder="Enter purpose"
                value={formik.values.link_purpose}
                required
                className={
                  formik.errors.link_purpose && formik.touched.link_purpose
                    ? "error"
                    : ""
                }
                onSlInput={(e) => {
                  formik.setFieldValue("link_purpose", e.target.value);
                }}
                onBlur={formik.handleBlur}
                name="link_purpose"
                style={{
                  "--sl-input-border-radius": "6px",
                  "--sl-input-border-width": "1px",
                  "--sl-input-border-color":
                    formik.errors.link_purpose && formik.touched.link_purpose
                      ? "#dc3545"
                      : "#ced4da",
                }}
              />
              {formik.errors.link_purpose && formik.touched.link_purpose && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px",
                    fontWeight: "500",
                  }}
                >
                  {formik.errors.link_purpose}
                </div>
              )}
            </div>
          </div>

          <div
            className="d-flex gap-2 mt-4"
            style={{
              marginTop: "32px",
              gap: "12px",
              justifyContent: "flex-end",
              paddingTop: "20px",
              borderTop: "1px solid #dee2e6",
            }}
          >
            <SlButton
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Adding..." : "Add Payment Link"}
            </SlButton>
            <SlButton
              type="button"
              variant="default"
              onClick={() => {
                setShowForm(false);
                formik.resetForm();
              }}
            >
              Cancel
            </SlButton>
          </div>
        </form>
        <div className="w-full ">
          OR
        </div>
        <div className="d-flex gap-2">
          <SlInput type="text" placeholder="Add Direct Link" value={addDirectLink} onSlInput={(e) => setAddDirectLink(e.target.value)} />
          <SlButton variant="primary" onClick={() => handleAddDirectLink()} disabled={!addDirectLink}>Add Direct Link</SlButton>
        </div>
      </SlDrawer>
    </div>
  );
}
export default CashfreePaymentLinkCreation;
